import React, { useState } from 'react';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import { StarterEntry } from './manifest';

// Scoped to .cak-code-pane so the app's global SCSS can't bleed onto the editor's
// highlighted token <span>s (which otherwise render every token inside a box).
// The editor (react-simple-code-editor) overlays a transparent <textarea> on a
// Prism-highlighted <pre>; both must keep a transparent background so the dark
// scroll container shows through and the caret aligns with the highlighted text.
const PRISM_THEME_CSS = `
.cak-code-pane pre,
.cak-code-pane textarea { background: transparent !important; border: 0 !important; box-shadow: none !important; }
/* Catch-all readable base color (incl. template-string, used by the gql\`\` query)
   with !important so the app's global syntax CSS can't override it to dark. */
.cak-code-pane .token { background: transparent !important; padding: 0 !important; border-radius: 0 !important; color: #abb2bf !important; }
.cak-code-pane textarea { caret-color: #f7fafc; outline: none !important; }
.cak-code-pane .token.comment, .cak-code-pane .token.prolog, .cak-code-pane .token.doctype, .cak-code-pane .token.cdata { color: #8e908c !important; }
.cak-code-pane .token.punctuation { color: #c0c5ce !important; }
.cak-code-pane .token.property, .cak-code-pane .token.tag, .cak-code-pane .token.boolean, .cak-code-pane .token.number, .cak-code-pane .token.constant,
.cak-code-pane .token.symbol, .cak-code-pane .token.deleted, .cak-code-pane .token.class-name, .cak-code-pane .token.function { color: #d19a66 !important; }
.cak-code-pane .token.selector, .cak-code-pane .token.attr-name, .cak-code-pane .token.string, .cak-code-pane .token.char, .cak-code-pane .token.builtin,
.cak-code-pane .token.inserted { color: #98c379 !important; }
.cak-code-pane .token.operator, .cak-code-pane .token.entity, .cak-code-pane .token.url, .cak-code-pane .language-css .token.string,
.cak-code-pane .style .token.string { color: #56b6c2 !important; }
.cak-code-pane .token.atrule, .cak-code-pane .token.attr-value, .cak-code-pane .token.keyword { color: #c678dd !important; }
.cak-code-pane .token.regex, .cak-code-pane .token.important, .cak-code-pane .token.variable { color: #e06c75 !important; }
`;

type Props = {
  entry: StarterEntry;
  code: string;
  onChange: (next: string) => void;
  onReset: () => void;
};

export default function CodePane({ entry, code, onChange, onReset }: Props) {
  const [copied, setCopied] = useState(false);
  const dirty = code !== entry.source;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entry.pattern}.tsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const btn = {
    padding: '0.5rem 0.75rem', borderRadius: 4, border: '1px solid #cbd5e0',
    cursor: 'pointer', fontSize: 13, fontWeight: 600,
  } as const;

  return (
    <div className="cak-code-pane" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <style>{PRISM_THEME_CSS}</style>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'center' }}>
        <button onClick={handleCopy} style={{ ...btn, background: copied ? '#48bb78' : '#4a5568', color: 'white' }}>
          {copied ? '✓ Copied' : '📋 Copy code'}
        </button>
        <button onClick={handleDownload} style={{ ...btn, background: 'white', color: '#4a5568' }}>
          ⬇ Download .tsx
        </button>
        <button
          onClick={onReset}
          disabled={!dirty}
          style={{ ...btn, background: 'white', color: dirty ? '#4a5568' : '#a0aec0', cursor: dirty ? 'pointer' : 'default' }}
        >
          ↺ Reset
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: dirty ? '#d69e2e' : '#a0aec0' }}>
          {dirty ? 'edited — click Preview to render' : 'editable'}
        </span>
      </div>
      <div style={{
        flex: 1, minHeight: 0, overflow: 'auto', overscrollBehavior: 'contain',
        background: '#282c34', borderRadius: 6,
      }}>
        <Editor
          value={code}
          onValueChange={onChange}
          highlight={(c) => Prism.highlight(c, Prism.languages.tsx as any, 'tsx')}
          padding={16}
          style={{
            fontFamily: 'Menlo, Monaco, "Courier New", monospace',
            fontSize: 12, lineHeight: 1.5, color: '#abb2bf', minHeight: '100%',
          }}
        />
      </div>
    </div>
  );
}
